import { ArrowLeftIcon, Button, Typography } from '@iheartjane/reefer';
import { formatCurrency } from '@iheartjane/utils';
import { clsx } from 'clsx';
import { useMemo } from 'react';

import { BagTransferModalSkeleton } from '@jane/bloom/components/skeletonLoaders/bagTransferModalSkeleton';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import type { Cart } from '@jane/bloom/viewModels/cart';

import { BagTransferItem } from './bagTransferItem';

import styles from './bagTransferContent.module.css';

interface BagTransferContentProps {
  newBag: Cart | undefined;
  oldStoreName?: string;
  onCancel: () => void;
  onConfirm: () => void;
  variant?: 'add-to-cart' | 'checkout' | 'wizard';
}

export function BagTransferContent({
  newBag,
  oldStoreName,
  onCancel,
  onConfirm,
  variant = 'add-to-cart',
}: BagTransferContentProps) {
  const { cart, pinnedStore } = useRootLoaderData();

  const isLoaded = !!(newBag && newBag.cart);

  const outOfStockProducts = useMemo(
    () =>
      isLoaded
        ? cart.items.filter((item) =>
            newBag.cart.unavailable_products.find(
              (product) => product.product_id === item.productId
            )
          )
        : [],
    [cart, isLoaded, newBag]
  );

  const quantityAdjustedProducts = useMemo(
    () =>
      isLoaded
        ? newBag.cart.products.filter(
            (product) =>
              !!newBag.cart.quantity_adjusted_products &&
              newBag.cart.quantity_adjusted_products.includes(
                product.product_id
              )
          )
        : [],
    [isLoaded, newBag]
  );

  const transferredProducts = useMemo(
    () =>
      isLoaded
        ? newBag.cart.products.filter(
            (product) => !quantityAdjustedProducts?.includes(product)
          )
        : [],
    [isLoaded, newBag, quantityAdjustedProducts]
  );

  const isCheckoutVariant = variant === 'checkout';
  const isWizardVariant = variant === 'wizard';

  const hasProducts = useMemo(
    () =>
      quantityAdjustedProducts.length > 0 ||
      transferredProducts.length > 1 ||
      (isCheckoutVariant && transferredProducts.length > 0),
    [
      isCheckoutVariant,
      quantityAdjustedProducts.length,
      transferredProducts.length,
    ]
  );

  const getModalTitle = () => {
    if (hasProducts) {
      return `Transfer your bag to ${pinnedStore.name}?`;
    }
    return isCheckoutVariant
      ? `Keep your bag at ${oldStoreName}?`
      : `Start a new bag at ${pinnedStore.name}?`;
  };

  const getModalMessage = () => {
    if (isCheckoutVariant) {
      return hasProducts
        ? `We see you've switched your location to ${pinnedStore.name} but your bag was created at ${oldStoreName}. Do you want to transfer your bag to ${pinnedStore.name}?`
        : `We see you've switched your location to ${pinnedStore.name} but your bag is not in stock at that location. Do you want to proceed with your current bag at ${oldStoreName}?`;
    }
    return hasProducts
      ? `You can only create one bag from one store at a time. Adding this product will transfer your current bag to ${pinnedStore.name} and remove any items not available at that location. Do you want to proceed?`
      : `You can only create one bag from one store at a time. None of the products in your bag are in stock at ${pinnedStore.name}, adding this product will require you to clear your current bag. Do you want to start a new bag with this item?`;
  };

  const getCancelButtonLabel = () => {
    if (isWizardVariant) {
      return 'Back to results';
    }
    if (isCheckoutVariant) {
      return hasProducts ? 'No, keep my bag' : 'Never mind, keep shopping';
    }
    return 'Never mind';
  };

  const getConfirmButtonLabel = () => {
    if (isWizardVariant) {
      return hasProducts ? 'Yes, transfer bag' : 'Yes, start new bag';
    }
    if (hasProducts) {
      return 'Yes, transfer my bag';
    }
    return isCheckoutVariant ? 'Yes, continue to bag' : 'Yes, start new bag';
  };

  return (
    <div
      className={clsx({
        [styles['wizardWrapper']]: isWizardVariant,
      })}
    >
      {isLoaded ? (
        <>
          <div className={styles['bagTransferModalHeader']}>
            <Typography
              variant="h2-semibold"
              mb={24}
              color="text-main"
              textAlign="center"
            >
              {getModalTitle()}
            </Typography>

            <Typography
              color="text-main"
              textAlign={isWizardVariant ? 'center' : undefined}
            >
              {getModalMessage()}
            </Typography>
          </div>
          {hasProducts && (
            <>
              {' '}
              <div className={styles['bagTransferModalDetails']}>
                <Typography variant="body-semibold">Your bag</Typography>
                <div className={styles['bagTransferModalAmountTag']}>
                  <Typography
                    variant="body-semibold"
                    strikeThrough
                    mr={4}
                    color="grays-mid"
                  >
                    {formatCurrency(Number(cart.orderTotal))}
                  </Typography>
                  <Typography variant="body-semibold">
                    {formatCurrency(Number(newBag.cart.order_total))}
                  </Typography>
                </div>
              </div>
              <div className={styles['bagTransferModalProducts']}>
                {outOfStockProducts.map((product) => (
                  <BagTransferItem
                    key={product.productId}
                    product={product}
                    isOutOfStock
                  />
                ))}
                {quantityAdjustedProducts?.map((product) => (
                  <BagTransferItem
                    key={product.product_id}
                    product={product}
                    oldProductCount={
                      cart.items.find(
                        (item) => item.productId === product.product_id
                      )?.count
                    }
                  />
                ))}
                {transferredProducts?.map((product) => (
                  <BagTransferItem key={product.product_id} product={product} />
                ))}
              </div>
            </>
          )}
        </>
      ) : (
        <BagTransferModalSkeleton />
      )}

      <div
        className={clsx(styles['bagTransferModalButtons'], {
          [styles['bagTransferModalButtons--wizard']]: isWizardVariant,
        })}
      >
        <Button
          full
          variant="secondary"
          label={getCancelButtonLabel()}
          loading={isCheckoutVariant && !isLoaded}
          onClick={onCancel}
          startIcon={isWizardVariant ? <ArrowLeftIcon /> : undefined}
        />
        <Button
          full
          variant="primary"
          label={getConfirmButtonLabel()}
          loading={!isLoaded}
          onClick={onConfirm}
          type="submit"
        />
      </div>
    </div>
  );
}
